import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import * as XLSX from 'xlsx';
import { API_BASE } from '../config/api';
import { resolveSession } from '../lib/session';
import { useSessionUser } from '../SessionContext';
import './Reports.css';

const PAGE_SIZE = 31;
const DEFAULT_ROUTE_POINTS = [{ siteName: 'CC', name: 'LAS', number: '3001' }];
const DEFAULT_POSTGRES_SQL = `WITH call_events AS (
  SELECT
    NULLIF(uniqueid, '') AS call_id,
    created_at AS event_at,
    created_at::date AS report_date,
    COALESCE(NULLIF(site_name, ''), 'CC') AS site_name,
    COALESCE(NULLIF(route_point_name, ''), 'LAS') AS route_point_name,
    COALESCE(NULLIF(route_point_number, ''), '3001') AS route_point_number,
    LOWER(COALESCE(queuestatus, '')) AS queue_status,
    LOWER(COALESCE(userfieldraw, '')) AS user_event,
    COALESCE(ivrtime, 0)::integer AS ivr_seconds,
    COALESCE(queueholdtime, 0)::integer AS queue_hold_seconds,
    COALESCE(ringtime, 0)::integer AS ring_seconds,
    COALESCE(oncalltime, 0)::integer AS oncall_seconds,
    oncallat
  FROM call_state_log
  WHERE created_at >= :start_date::date
    AND created_at < (:end_date::date + INTERVAL '1 day')
),
logical_calls AS (
  SELECT
    call_id,
    MIN(report_date) AS report_date,
    MIN(site_name) AS site_name,
    MIN(route_point_name) AS route_point_name,
    MIN(route_point_number) AS route_point_number,
    MAX(CASE
      WHEN oncallat IS NOT NULL
        OR oncall_seconds > 0
        OR queue_status IN ('answered', 'answer', 'connected', 'complete', 'completed')
      THEN 1 ELSE 0
    END) AS answered,
    MAX(CASE
      WHEN queue_status <> ''
        OR user_event LIKE 'event_queue%'
      THEN 1 ELSE 0
    END) AS hit_queue,
    GREATEST(
      MAX(GREATEST(ivr_seconds, queue_hold_seconds, ring_seconds, 0)),
      COALESCE(EXTRACT(EPOCH FROM (MAX(event_at) - MIN(event_at))), 0)::integer
    ) AS termination_seconds
  FROM call_events
  WHERE call_id IS NOT NULL
  GROUP BY call_id
)
SELECT
  report_date,
  site_name,
  route_point_name,
  route_point_number,
  COUNT(*) AS offered,
  SUM(answered) AS answered,
  SUM(CASE WHEN answered = 0 AND hit_queue = 0 THEN 1 ELSE 0 END) AS terminated,
  ROUND(AVG(CASE WHEN answered = 0 AND hit_queue = 0 THEN termination_seconds END), 2) AS avg_time_to_termination_seconds,
  ROUND((SUM(CASE WHEN answered = 0 AND hit_queue = 0 THEN 1 ELSE 0 END)::numeric / NULLIF(COUNT(*), 0)) * 100, 2) AS pct_terminated,
  SUM(CASE WHEN answered = 0 AND hit_queue = 1 THEN 1 ELSE 0 END) AS abandoned,
  ROUND((SUM(CASE WHEN answered = 0 AND hit_queue = 1 THEN 1 ELSE 0 END)::numeric / NULLIF(COUNT(*), 0)) * 100, 2) AS pct_abandoned
FROM logical_calls
GROUP BY report_date, site_name, route_point_name, route_point_number
ORDER BY route_point_number ASC, report_date ASC;`;

const emptyMetric = {
  offered: 0,
  answered: 0,
  terminated: 0,
  avgTimeToTerminationSeconds: 0,
  pctTerminated: 0,
  abandoned: 0,
  pctAbandoned: 0,
};

const toIsoDate = (date) => {
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${date.getFullYear()}-${month}-${day}`;
};

const todayIso = () => toIsoDate(new Date());

const monthStartIso = () => {
  const date = new Date();
  date.setDate(1);
  return toIsoDate(date);
};

const normalizeText = (value) => String(value ?? '').trim();

const formatDate = (value) => {
  const text = normalizeText(value);
  if (!text) return '';
  const [year, month, day] = text.slice(0, 10).split('-');
  if (!year || !month || !day) return text;
  return `${Number(month)}/${Number(day)}/${year}`;
};

const formatNumber = (value) => Math.round(Number(value) || 0).toLocaleString();
const formatDecimal = (value) => (Number(value) || 0).toFixed(2);

const formatDuration = (seconds) => {
  const totalSeconds = Math.max(0, Math.round(Number(seconds) || 0));
  const minutes = Math.floor(totalSeconds / 60);
  const remSeconds = totalSeconds % 60;
  return `${minutes}:${String(remSeconds).padStart(2, '0')}`;
};

const metricCells = (metric = emptyMetric) => [
  formatNumber(metric.offered),
  formatNumber(metric.answered),
  formatNumber(metric.terminated),
  formatDecimal(metric.pctTerminated),
  formatDuration(metric.avgTimeToTerminationSeconds),
  formatNumber(metric.abandoned),
  formatDecimal(metric.pctAbandoned),
];

const authHeaders = () => {
  const token = localStorage.getItem('token');
  return {
    Accept: 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };
};

const normalizeMetric = (source = {}) => ({
  offered: Number(source.offered ?? source.Offered ?? 0) || 0,
  answered: Number(source.answered ?? source.Answered ?? 0) || 0,
  terminated: Number(source.terminated ?? source.Terminated ?? 0) || 0,
  avgTimeToTerminationSeconds: Number(
    source.avgTimeToTerminationSeconds ??
    source.avg_time_to_termination_seconds ??
    source.AvgTimeToTerminationSeconds ??
    0
  ) || 0,
  pctTerminated: Number(source.pctTerminated ?? source.pct_terminated ?? source.PctTerminated ?? 0) || 0,
  abandoned: Number(source.abandoned ?? source.Abandoned ?? 0) || 0,
  pctAbandoned: Number(source.pctAbandoned ?? source.pct_abandoned ?? source.PctAbandoned ?? 0) || 0,
});

const normalizeRoutePoint = (source = {}) => ({
  siteName: normalizeText(source.siteName ?? source.site_name ?? source.SiteName ?? 'CC') || 'CC',
  name: normalizeText(source.name ?? source.routePointName ?? source.route_point_name ?? source.RoutePointName ?? ''),
  number: normalizeText(source.number ?? source.routePointNumber ?? source.route_point_number ?? source.RoutePointNumber ?? ''),
});

const normalizeSummary = (source = {}) => ({
  ...normalizeRoutePoint(source),
  ...normalizeMetric(source),
});

const normalizeDetailRow = (source = {}) => ({
  date: normalizeText(source.date ?? source.reportDate ?? source.report_date ?? source.ReportDate ?? ''),
  ...normalizeRoutePoint(source),
  ...normalizeMetric(source),
});

const buildReportPath = (filters, page = 1) => {
  const params = new URLSearchParams();
  params.set('startDate', filters.startDate);
  params.set('endDate', filters.endDate);
  params.set('page', String(page));
  params.set('perPage', String(PAGE_SIZE));
  return `/reports/cdn-route-point-statistics?${params.toString()}`;
};

const fetchJson = async (path) => {
  const response = await fetch(`${API_BASE}${path}`, { headers: authHeaders() });
  const text = await response.text();
  let json = null;
  try {
    json = text ? JSON.parse(text) : null;
  } catch {
    json = null;
  }

  if (!response.ok || json?.ok === false || json?.success === false) {
    const message = json?.error || json?.message || text || `Request failed: ${response.status}`;
    if (response.status === 404 && String(message).toLowerCase().includes('route')) {
      throw new Error('Laravel report API route missing. Register GET /api/reports/cdn-route-point-statistics in routes/api.php.');
    }
    throw new Error(message);
  }
  return json;
};

const Reports = () => {
  const navigate = useNavigate();
  const { user: sessionUser } = useSessionUser();
  const { username, roleName } = useMemo(() => resolveSession(sessionUser), [sessionUser]);
  const [filters, setFilters] = useState({
    startDate: monthStartIso(),
    endDate: todayIso(),
  });
  const [report, setReport] = useState({
    totals: emptyMetric,
    routePointSummaries: [],
    detail: { rows: [], allRows: [], page: 1, perPage: PAGE_SIZE, totalRows: 0, totalPages: 1 },
    reportInterval: '',
    sqlOutput: '',
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const activeSiteName = report.routePointSummaries?.[0]?.siteName || DEFAULT_ROUTE_POINTS[0].siteName;

  const routeGroups = useMemo(() => (
    (report.routePointSummaries || []).map((summary) => ({
      summary,
      rows: (report.detail?.rows || []).filter((row) => row.number === summary.number),
    }))
  ), [report]);

  const loadReport = useCallback(async (nextFilters = filters, page = 1) => {
    if (!nextFilters.startDate || !nextFilters.endDate) {
      setError('Start Date and End Date are required.');
      return;
    }
    if (nextFilters.startDate > nextFilters.endDate) {
      setError('Start Date cannot be after End Date.');
      return;
    }

    try {
      setLoading(true);
      setError('');
      const json = await fetchJson(buildReportPath(nextFilters, page));
      const detail = json?.detail || json?.data?.detail || {};
      const rows = Array.isArray(detail.rows) ? detail.rows : (Array.isArray(json?.rows) ? json.rows : []);
      const allRows = Array.isArray(detail.allRows) ? detail.allRows : rows;
      setReport({
        totals: normalizeMetric(json?.totals || json?.data?.totals || {}),
        routePointSummaries: (json?.routePointSummaries || json?.data?.routePointSummaries || []).map(normalizeSummary),
        detail: {
          rows: rows.map(normalizeDetailRow),
          allRows: allRows.map(normalizeDetailRow),
          page: Number(detail.page || page),
          perPage: Number(detail.perPage || PAGE_SIZE),
          totalRows: Number(detail.totalRows || allRows.length || rows.length),
          totalPages: Number(detail.totalPages || 1),
        },
        reportInterval: json?.reportInterval || json?.data?.reportInterval || '',
        sqlOutput: json?.sqlOutput || json?.data?.sqlOutput || '',
      });
    } catch (err) {
      setReport((prev) => ({
        ...prev,
        totals: emptyMetric,
        routePointSummaries: [],
        detail: { rows: [], allRows: [], page: 1, perPage: PAGE_SIZE, totalRows: 0, totalPages: 1 },
      }));
      setError(err?.message || 'Failed to load report from PostgreSQL API.');
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    document.body.setAttribute('data-app', 'reports');
    return () => document.body.removeAttribute('data-app');
  }, []);

  useEffect(() => {
    loadReport(filters, 1);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const updateField = (event) => {
    const { name, value } = event.target;
    setFilters((prev) => ({ ...prev, [name]: value }));
  };

  const exportExcel = () => {
    const workbook = XLSX.utils.book_new();
    const summaryRows = [
      ['CDN (Route Point) Statistics'],
      ['Report Interval', report.reportInterval || `${filters.startDate} - ${filters.endDate}`],
      ['Site Name', activeSiteName],
      [],
      ['Grand Total', 'Offered', 'Answered', 'Terminated', '% Terminated', 'Avg Time To Termination', 'Abandoned', '% Abandoned'],
      ['', ...metricCells(report.totals)],
      [],
      ['Route Point Summary'],
      ['CDN (Route Point)', 'Offered', 'Answered', 'Terminated', '% Terminated', 'Avg Time To Termination', 'Abandoned', '% Abandoned'],
      ...(report.routePointSummaries || []).map((summary) => [`${summary.name} - ${summary.number}`, ...metricCells(summary)]),
    ];
    const detailRows = [
      ['Date', 'Site Name', 'CDN Name', 'Route Point Number', 'Offered', 'Answered', 'Terminated', '% Terminated', 'Avg Time To Termination', 'Abandoned', '% Abandoned'],
      ...(report.detail?.allRows || []).map((row) => [
        formatDate(row.date),
        row.siteName,
        row.name,
        row.number,
        row.offered,
        row.answered,
        row.terminated,
        Number(row.pctTerminated || 0),
        formatDuration(row.avgTimeToTerminationSeconds),
        row.abandoned,
        Number(row.pctAbandoned || 0),
      ]),
    ];
    XLSX.utils.book_append_sheet(workbook, XLSX.utils.aoa_to_sheet(summaryRows), 'Summary');
    XLSX.utils.book_append_sheet(workbook, XLSX.utils.aoa_to_sheet(detailRows), 'Daily Detail');
    XLSX.writeFile(workbook, `cdn-route-point-statistics-${filters.startDate}-${filters.endDate}.xlsx`);
  };

  const currentPage = Number(report.detail?.page || 1);
  const totalPages = Number(report.detail?.totalPages || 1);
  const totalRows = Number(report.detail?.totalRows || 0);

  return (
    <div className="cdn-report-shell">
      <header className="cdn-report-topbar no-print">
        <div>
          <strong>SLACC</strong>
          <span>Sindh Legal Advisory Call Center</span>
        </div>
        <div>
          <h1>CDN (Route Point) Statistics</h1>
          <p>PostgreSQL MIS report</p>
        </div>
        <div className="cdn-report-user-actions">
          <span>{username ? `${username}${roleName ? ` - ${roleName}` : ''}` : 'Report Module'}</span>
          <button type="button" className="cdn-btn soft" onClick={() => navigate('/reports/dnis-statistics')}>DNIS Report</button>
          <button type="button" className="cdn-btn soft" onClick={() => navigate('/reports/daily-call-report')}>Daily Call Report</button>
          <button type="button" className="cdn-btn soft" onClick={() => navigate('/reports/agent-state-report')}>Agent State</button>
          <button type="button" className="cdn-btn soft" onClick={() => navigate('/reports/agent-login-logout-report')}>Login/Logout</button>
          <button type="button" className="cdn-btn soft" onClick={() => navigate('/reports/agent-performance-report')}>Performance</button>
          <button type="button" className="cdn-btn soft" onClick={() => navigate('/backoffice')}>Back Office</button>
          <button type="button" className="cdn-btn danger" onClick={() => navigate('/logout')}>Logout</button>
        </div>
      </header>

      <section className="cdn-filter-panel no-print">
        <div className="cdn-filter-grid">
          <label><span>Start Date</span><input type="date" name="startDate" value={filters.startDate} onChange={updateField} /></label>
          <label><span>End Date</span><input type="date" name="endDate" value={filters.endDate} onChange={updateField} /></label>
          <div className="cdn-filter-actions">
            <button type="button" className="cdn-btn primary" onClick={() => loadReport(filters, 1)} disabled={loading}>
              {loading ? 'Loading' : 'Show'}
            </button>
            <button type="button" className="cdn-btn success" onClick={exportExcel} disabled={!report.detail?.allRows?.length}>Excel</button>
            <button type="button" className="cdn-btn soft" onClick={() => window.print()} disabled={!report.detail?.allRows?.length}>PDF</button>
          </div>
        </div>
        {error ? <div className="cdn-alert">{error}</div> : null}
      </section>

      <main className="cdn-report-paper">
        <section className="cdn-report-heading">
          <h2>CDN (Route Point) Statistics</h2>
          <p><strong>Report Interval:</strong> {report.reportInterval || `${filters.startDate} 00:00:00 - ${filters.endDate} 23:59:59`}</p>
          <p><strong>Site Name:</strong> {activeSiteName}</p>
        </section>

        <section className="cdn-grand-total">
          <table>
            <thead>
              <tr>
                <th></th><th>Offered</th><th>Answered</th><th>Terminated</th><th>% Terminated</th>
                <th>Avg Time To Termination</th><th>Abandoned</th><th>% Abandoned</th>
              </tr>
            </thead>
            <tbody>
              <tr className="cdn-total-row">
                <td>Grand Total</td>
                {metricCells(report.totals).map((cell, index) => <td key={index}>{cell}</td>)}
              </tr>
            </tbody>
          </table>
        </section>

        {routeGroups.length === 0 ? (
          <div className="cdn-empty">{loading ? 'Loading report...' : 'No report data found.'}</div>
        ) : routeGroups.map(({ summary, rows }) => (
          <section className="cdn-route-section" key={summary.number}>
            <h3>CDN (Route Point) Name - Number: {summary.name} - {summary.number}</h3>
            <table className="cdn-report-table">
              <thead>
                <tr className="cdn-summary-row">
                  <th>Summary</th>
                  {metricCells(summary).map((cell, index) => <th key={index}>{cell}</th>)}
                </tr>
                <tr>
                  <th>Date</th><th>Offered</th><th>Answered</th><th>Terminated</th><th>% Terminated</th>
                  <th>Avg Time To Termination</th><th>Abandoned</th><th>% Abandoned</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <tr key={`${row.number}-${row.date}`}>
                    <td>{formatDate(row.date)}</td>
                    {metricCells(row).map((cell, index) => <td key={index}>{cell}</td>)}
                  </tr>
                ))}
                <tr className="cdn-total-row">
                  <td>CDN (Route Point): {summary.name} - {summary.number}</td>
                  {metricCells(summary).map((cell, index) => <td key={index}>{cell}</td>)}
                </tr>
              </tbody>
            </table>
          </section>
        ))}
      </main>

      <section className="cdn-pager no-print">
        <button type="button" className="cdn-btn soft" disabled={loading || currentPage <= 1} onClick={() => loadReport(filters, currentPage - 1)}>Previous</button>
        <span>Page {currentPage} of {totalPages} - {formatNumber(totalRows)} daily rows</span>
        <button type="button" className="cdn-btn soft" disabled={loading || currentPage >= totalPages} onClick={() => loadReport(filters, currentPage + 1)}>Next</button>
      </section>

      <section className="cdn-sql-panel no-print">
        <details>
          <summary>PostgreSQL SQL Output</summary>
          <pre>{report.sqlOutput || DEFAULT_POSTGRES_SQL}</pre>
        </details>
      </section>
    </div>
  );
};

export default Reports;
